import { GameConfig, type Obstacle } from './game-types';

// Tracked per upper variant so lamps don't block plungers. Lamps are
// decorative (their hitbox sits above the jump-peak, they never touch the
// player), so the "no two consecutive uppers" rule only needs to prevent
// plunger → plunger (tight double-duck) and lamp → lamp (visually noisy).
export type LastObstacleType = 'floor' | 'plunger' | 'lamp' | null;

export interface SpawnContext {
  obstacles: Obstacle[];
  lastObstacleType: LastObstacleType;
  lastCartAt: number;
  now: number;
}

export interface SpawnDecision {
  obstacle: Obstacle | null;
  lastObstacleType: LastObstacleType;
  lastCartAt: number;
}

// ---------- Presence predicates ----------

export const hasCart = (obstacles: Obstacle[]): boolean =>
  obstacles.some((o) => o.type === 'cart');

export const hasPlunger = (obstacles: Obstacle[]): boolean =>
  obstacles.some((o) => o.type === 'plunger');

// A cone still ahead of (or overlapping) the player forces a jump whose
// landing window overlaps the arrival of any faster follow-up obstacle.
// Cart (1.6×) and plunger (1.5×) both catch up and land the player inside
// the 37-frame jump arc at every game speed — unwinnable.
export const hasConeAhead = (obstacles: Obstacle[]): boolean =>
  obstacles.some(
    (o) => o.type === 'floor' && o.x + o.width > GameConfig.PLAYER_X,
  );

// ---------- Spawn-eligibility rules ----------
// Each rule encodes one playability invariant. Keep one reason per rule so
// regressions are easy to diagnose.

export const canSpawnCart = (ctx: SpawnContext): boolean =>
  !hasCart(ctx.obstacles) &&
  !hasPlunger(ctx.obstacles) &&
  !hasConeAhead(ctx.obstacles) &&
  ctx.now - ctx.lastCartAt > GameConfig.CART_COOLDOWN_MS;

export const canSpawnPlunger = (ctx: SpawnContext): boolean =>
  !hasCart(ctx.obstacles) &&
  !hasConeAhead(ctx.obstacles) &&
  ctx.lastObstacleType !== 'plunger';

export const canSpawnLamp = (ctx: SpawnContext): boolean =>
  ctx.lastObstacleType !== 'lamp';

// Cones are skipped while a cart is on screen — the cart would catch the
// cone and the timing near the player becomes unreactive.
// Cones are also skipped while another cone is still ahead of the player:
// cone-after-cone is a self-sustaining pileup. Each new cone extends the
// "cone ahead" flag, which blocks cart and plunger spawns, and the 70%
// fallback would keep picking cone — the field degenerates into cones only.
export const canSpawnCone = (ctx: SpawnContext): boolean =>
  !hasCart(ctx.obstacles) && !hasConeAhead(ctx.obstacles);

// ---------- Obstacle factories ----------

const makeCart = (): Obstacle => ({
  x: GameConfig.CANVAS_WIDTH,
  y: GameConfig.GROUND_Y - GameConfig.CART_HEIGHT,
  width: GameConfig.CART_WIDTH,
  height: GameConfig.CART_HEIGHT,
  type: 'cart',
  speedMul: GameConfig.CART_SPEED_MUL,
});

const makePlunger = (): Obstacle => ({
  x: GameConfig.CANVAS_WIDTH,
  y: GameConfig.PLUNGER_Y,
  width: GameConfig.PLUNGER_WIDTH,
  height: GameConfig.PLUNGER_HEIGHT,
  type: 'plunger',
  speedMul: GameConfig.PLUNGER_SPEED_MUL,
});

const makeLamp = (): Obstacle => ({
  x: GameConfig.CANVAS_WIDTH,
  y: GameConfig.LAMP_HANG_Y,
  width: GameConfig.LAMP_WIDTH,
  height: GameConfig.LAMP_HEIGHT,
  type: 'upper',
});

const makeCone = (): Obstacle => ({
  x: GameConfig.CANVAS_WIDTH,
  y: GameConfig.GROUND_Y - GameConfig.CONE_HEIGHT,
  width: GameConfig.CONE_WIDTH,
  height: GameConfig.CONE_HEIGHT,
  type: 'floor',
});

// ---------- Decision ----------
// Priority: cart → plunger → lamp → cone. Returns a null obstacle when
// every rule forbids a spawn this tick; the caller still advances its
// spawn-interval counter.

const LAMP_CHANCE = 0.3;

export const decideNextObstacle = (
  ctx: SpawnContext,
  random: () => number = Math.random,
): SpawnDecision => {
  const keep: SpawnDecision = {
    obstacle: null,
    lastObstacleType: ctx.lastObstacleType,
    lastCartAt: ctx.lastCartAt,
  };

  if (canSpawnCart(ctx) && random() < GameConfig.CART_SPAWN_CHANCE) {
    return { ...keep, obstacle: makeCart(), lastCartAt: ctx.now };
  }

  if (canSpawnPlunger(ctx) && random() < GameConfig.PLUNGER_SPAWN_CHANCE) {
    return { ...keep, obstacle: makePlunger(), lastObstacleType: 'plunger' };
  }

  if (canSpawnLamp(ctx) && random() < LAMP_CHANCE) {
    return { ...keep, obstacle: makeLamp(), lastObstacleType: 'lamp' };
  }

  if (canSpawnCone(ctx)) {
    return { ...keep, obstacle: makeCone(), lastObstacleType: 'floor' };
  }

  return keep;
};
